<template>
	<div class="p-grid">
		<div class="p-col-12">
			<div class="card">

        <Toolbar class="p-mb-4">
          <template #start>
            <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('Services') }}</div>
            <AddNewButton v-show="canModerate" :label="$t('Add')" @click="addNewItem" class="p-mr-2"/>
            <Button class="p-button-secondary p-button-raised"
                    @click="changeItemsCategoryOnClick"
                    icon="pi pi-file-edit"
                    v-show="canModerate"
                    :disabled="!selectedItems?.length || dataTableIsLoading || billingDataEditingLocked"
                    :label="selectedItems?.length
                        ? `${$t('changeCategoryLabel')} (${selectedItems.length})`
                        : $t('changeCategoryLabel')"/>
          </template>
          <template #end>
            <CustomDropdown id="service-category-id"
                            class="p-mr-2"
                            ref="selectedCategoryDropdown"
                            style="min-width: 180px; max-width: 200px;"
                            data-key="id"
                            v-model="selectedCategory"
                            :options="categoriesList"
                            :disabled="dataTableIsLoading"
                            :filterPlaceholder="$t('findCategoryPlaceholder')"
                            optionLabel="name"
                            :filter="true"
                            :filterFields="['name']"
                            @change="changeCategory"
                            :showClear="true">
              <template #value="slotProps">
                <div v-if="slotProps.value">
                  <div>{{ slotProps.value.name }}<span v-if="slotProps.value.count"> ({{slotProps.value.count}})</span></div>
                </div>
                <span v-else>{{ $t('selectCategoryPlaceholder') }}</span>
              </template>
              <template #option="slotProps">
                <div v-if="slotProps.option">
                  <div :class="{'bold-text' : slotProps.option.id === 0}">{{ slotProps.option.name }}<span v-if="slotProps.option.count || slotProps.option.count === 0"> ({{slotProps.option.count}})</span></div>
                </div>
              </template>
            </CustomDropdown>
            <SearchInput :disabled="dataTableIsLoading" @change-processed-search-value="changeProcessedSearchValue" v-model="searchData" @show-spinner="isLoading = true"/>
          </template>
        </Toolbar>

        <Spinner v-if="isLoading"/>
        <DesktopDataTable v-else
                          :showHeader="false"
                          :dataTableIsLoading="dataTableIsLoading"
                          :itemsArrayIsEmpty="itemsArrayIsEmpty"
                          :sort="sort"
                          :showSearchInput="false"
                          :actionButtonsCount="actionButtonsCount"
                          @sort-items="sortItems"
                          :headers="[
                            {name: 'code', title: $t('Code'), sortable: true},
                            {name: 'name', title: $t('Name'), width: '30%', sortable: true},
                            {name: 'category', title: $t('Category'), sortable: false},
                            // {name: 'time', title: $t('Time'), sortable: true},
                            {name: 'time', title: $t('Duration'), sortable: true},
                            {name: 'purchase', title: $t('purchasePriceShortLabel'), sortable: false},
                            {name: 'sell', title: $t('sellingPriceShortLabel'), sortable: false},
                            {name: 'consumables_sell', title: $t('consumablesLabel'), isHidden: !canUseConsumables, sortable: false},
                            {name: 'created_at', title: $t('Created'), sortable: true},
                          ]">
          <template v-slot:multiselectHeader>
            <th v-show="canModerate" class="multiSelect-column-header">
              <Checkbox @click="selectAllCheckboxOnClick"
                        :class="{ 'billing-selection-checkbox-disabled': billingDataEditingLocked }"
                        :disabled="dataTableIsLoading || billingDataEditingLocked"
                        :value="0"
                        v-model="selectAllCheckbox"/>
            </th>
          </template>
          <template v-slot:body>
            <tr v-for="item of items"
                :key="item.id"
                :class="{'p-highlight': isItemSelected(item.id), 'updated-item': isItemUpdated(item.id)}">
              <td v-show="canModerate">
                <Checkbox :value="item.id"
                          :class="{ 'billing-selection-checkbox-disabled': billingDataEditingLocked }"
                          :disabled="dataTableIsLoading || billingDataEditingLocked"
                          v-model="selectedItems"/>
              </td>
              <td>
                <span v-if="item.code">{{ item.code }}</span>
                <i v-else class="ti-minus"></i>
              </td>
              <td>
                <span v-if="item.name">{{ item.name }}</span>
                <i v-else class="ti-minus"></i>
              </td>
              <td>
                <span v-if="item.service_category_id">{{ categoriesList?.find(c => c.id === item.service_category_id)?.name }}</span>
                <i v-else class="ti-minus"></i>
              </td>
              <td>
                <span v-if="item.time && +item.time !== 0">{{ formatHoursAndMinutes(item.time) }}</span>
                <i v-else class="ti-minus"></i>
              </td>

              <td>
                <span>{{ formatCurrency(item.purchase, 2, 4) }}</span>
              </td>

              <td>
                <span>{{ formatCurrency(item.sell, 2, 4) }}</span>
              </td>

              <td v-if="canUseConsumables">
                <span v-if="item.consumables_sell">{{ formatCurrency(item.consumables_sell) }}</span>
                <i v-else class="ti-minus"></i>
              </td>

              <td class="table-date-line">
                <span v-if="item.created_at">
                  {{ formatBy(item.created_at, item.createdBy) }}
                </span>
              </td>
              <td v-show="actionButtonsCount">
                <div class="actions">
                  <EditButton v-show="canModerate"
                              :disabled="dataTableIsLoading" @click="editItem(item)"/>
                  <DeleteButton v-show="canModerate"
                                @click="confirmItemDelete(item)"
                                :disabled="dataTableIsLoading"/>
                </div>
              </td>
            </tr>
          </template>
        </DesktopDataTable>

        <OwnPaginator v-show="!itemsArrayIsEmpty"
                      :rows="pagination.perPage"
                      :currentPage="pagination.currentPage - 1"
                      :totalPages="pagination.pageCount"
                      :totalRecords="pagination.totalCount"
                      :rowsPerPageOptions="[5,10,25,50,100,200]"
                      :pageLinkSize="11"/>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

        <ServiceModal :visible="itemModal"
                      :item="item"
                      :categories="categories"
                      @update-items="updateItems"
                      @close="closeItemModal"/>

        <ChangeCategoryModal :visible="changeCategoryModal"
                             :servicesCategoryModal="true"
                             :categories="categories"
                             :selectedItems="selectedItems"
                             @change-items-category="changeItemsCategory"
                             @close="closeChangeCategoryModal"/>

        <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                            :modalDataIsLoading="modalDataIsLoading"
                            @close="closeConfirmItemDeleteModal"
                            @delete-item="deleteItem"
                            :data="itemToDeleteData"/>
			</div>

		</div>
	</div>
</template>

<script>
import httpClient from '@/services/http.services'
import ServiceModal from '@/pages/data_directory/components/ServiceModal'
import datatableMixins from "@/mixins/datatableMixins";
import formatMixins from "@/mixins/formatMixins";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import ChangeCategoryModal from "@/components/ChangeCategoryModal.vue";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import billingRestrictionsMixin from '@/mixins/billingRestrictionsMixin'

export default {
  mixins: [ datatableMixins,showErrorsMixins,formatMixins, billingFeaturesMixins, billingRestrictionsMixin ],
  components: { ServiceModal,ChangeCategoryModal },
	data() {
		return {
      changeCategoryModal: false,
      taxes: null,
      selectedCategory: null,
      selectedCategoryId: null,
      categoriesList: [],
      categories: [],
      fieldsExpand: 'createdBy',
      apiLink: 'service',
      deleteItemUrl: 'service/delete',
      routePath: '/services',
      sort: {
        column: 'id',
        direction: '-',
      },
      totalServicesCount: null,
      categoryModal: false,
			category: {},
		}
	},
  watch: {
    '$i18n.locale'() {
      this.translateUncategorizedCategory()
    },
    '$store.state.firstLayerIsOpened'() {
      if (!this.$store.state.firstLayerIsOpened) {
        if (this.categoryModal) {
          this.closeCategoryModal()
        }
        if (this.changeCategoryModal) {
          this.closeChangeCategoryModal()
        }
      }
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    this.getPageData()
	},
  methods: {
    getPageData() {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      try {
        this.getCategories().then(() => this.getItems())
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
      }
    },
    translateUncategorizedCategory() {
      const uncategorizedCategory = this.categoriesList?.find(c => c.id === 0)
      if (uncategorizedCategory) {
        uncategorizedCategory.name = this.$t('Uncategorized')
      }
    },
    async actualizeSorting() {
      try {
        const { status } = await httpClient('service/set-sorting')
        if (status === 200) {
          this.$toast.add({severity:'success', detail: this.$t('Data updated'), life: settings.toastLife})
        }
      } catch (err) {
        this.showError(err)
      }
    },
    changeItemsCategory() {
      this.updatedItemId = null
      this.updatedItemsIds = [ ...this.selectedItems ]
      this.selectedItems = []
      this.getPageData()
      this.closeChangeCategoryModal()
    },
    changeItemsCategoryOnClick() {
      if (this.billingActionIsRestricted()) return false

      this.changeCategoryModal = true
      this.$store.commit('toggleFirstLayer', true)
    },
    closeChangeCategoryModal() {
      this.changeCategoryModal = false
      this.$store.commit('toggleFirstLayer', false)
      this.submitted = false
    },
    changeCategory() {
      this.selectedCategoryId = this.selectedCategory?.id

      if (!this.selectedCategory) this.$refs.selectedCategoryDropdown?.clearFilterValue()
      const route = {
        route: this.$route.path,
        query: {
          ...this.$route.query,
          category: this.selectedCategoryId,
        }
      }

      if (!this.selectedCategoryId && this.selectedCategoryId !== 0) {
        delete route.query.category
      }
      this.$router.push(route)
    },
    async getCategories() {
      if (!this.userCanGetData) return
      // console.log('get categories')
      try {
        const { status, data } = await httpClient.post('service/get-dropdown-categories?sort=sorting&expand=servicesCount', {
          getUncategorizedCount: true,
          // showNotActive: this.showNotActive
        })
        if (status === 200 && data) {
          this.categories = data.categories ?? []
          this.categoriesList = data.categories?.map(c => {
            return {
              id: +c.id,
              name: c.name,
              count: c.servicesCount ? +c.servicesCount :  0
            }
          })

          const uncategorizedCount = data.uncategorized_count ? +data.uncategorized_count : 0

          // if (uncategorizedCount) {
            this.categoriesList.push({id: 0, name: this.$t('Uncategorized'), count: uncategorizedCount })
          // }

          if (this.selectedCategoryId || this.selectedCategoryId === 0) {
            this.selectedCategory = this.categoriesList?.find(c => c.id === this.selectedCategoryId)
          } else {
            this.selectedCategory = null
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      }
    },
    async getItems() {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      // this.itemsLoadingStarted = true

      const ctrl = this.setController?.('services')
      try {
        // const { status, data, headers } = await httpClient(`${this.apiLink}?categoryId=${this.selectedCategoryId}&page=${this.pagination.currentPage}&per-page=${this.pagination.perPage}&sort=${this.sort.direction + this.sort.column}&search=${this.searchData}&expand=${this.fieldsExpand}`)
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            categoryId: this.selectedCategoryId,
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand
          },
          signal: ctrl?.signal
        })
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }
          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        if (this._abortors?.services === ctrl) {
          this._abortors.services = null
        }
      }
    },
    closeCategoryModal() {
      this.categoryModal = false
      this.$store.commit('toggleFirstLayer', false)
      this.submitted = false
    },
	},
  computed: {
    canModerate() {
      return this.$store.getters.isAccountantOrHigher
    },
    actionButtonsCount() {
      return this.canModerate ? 2 : 0
    },
    itemToDeleteData() {
      return this.itemToDelete?.name ?? null
    },
  }
}
</script>

<style scoped lang="scss">
td {
  height: 44px
}

:deep(.billing-selection-checkbox-disabled),
:deep(.billing-selection-checkbox-disabled .p-checkbox-box),
:deep(.billing-selection-checkbox-disabled input) {
  cursor: not-allowed !important;
}
</style>